"use client";

import { useEffect, useRef } from "react";
import { reportClientError } from "@/lib/errors/client";

type ReporterProps = {
  error?: Error & { digest?: string };
  reset?: () => void;
};

const isDev = process.env["NODE_ENV"] === "development";

export default function ErrorReporter({ error, reset }: ReporterProps) {
  const lastOverlayMsg = useRef("");
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    const inIframe = window.parent !== window;
    if (!inIframe) return;

    const targetOrigin = window.location.origin;
    const ancestorOrigin = window.location.ancestorOrigins?.[0];
    const isSameOriginParent = !ancestorOrigin || ancestorOrigin === targetOrigin;
    if (!isSameOriginParent) return;

    const send = (payload: unknown) => {
      try {
        window.parent.postMessage(payload, targetOrigin);
      } catch {
        /* no-op if blocked */
      }
    };

    const onError = (e: ErrorEvent) =>
      send({
        type: "ERROR_CAPTURED",
        error: {
          message: e.message?.slice(0, 2000),
          stack: e.error?.stack?.slice(0, 4000),
          filename: e.filename,
          lineno: e.lineno,
          colno: e.colno,
          source: "window.onerror",
        },
        timestamp: Date.now(),
      });

    const onReject = (e: PromiseRejectionEvent) =>
      send({
        type: "ERROR_CAPTURED",
        error: {
          message: e.reason?.message ? String(e.reason.message).slice(0, 2000) : String(e.reason).slice(0, 2000),
          stack: e.reason?.stack?.slice(0, 4000),
          source: "unhandledrejection",
        },
        timestamp: Date.now(),
      });

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onReject);

    if (isDev) {
      const pollOverlay = () => {
        const overlay = document.querySelector("[data-nextjs-dialog-overlay]");
        const node =
          overlay?.querySelector("h1, h2, .error-message, [data-nextjs-dialog-body]") ?? null;
        const txt = (node?.textContent ?? "").slice(0, 2000);
        if (txt && txt !== lastOverlayMsg.current) {
          lastOverlayMsg.current = txt;
          send({
            type: "ERROR_CAPTURED",
            error: { message: txt, source: "nextjs-dev-overlay" },
            timestamp: Date.now(),
          });
        }
      };
      pollRef.current = window.setInterval(pollOverlay, 2000) as unknown as number;
    }

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onReject);
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!error) return;
    reportClientError(error, { tags: { source: "global-error" } });
    try {
      if (window.parent !== window) {
        const targetOrigin = window.location.origin;
        const ancestorOrigin = window.location.ancestorOrigins?.[0];
        if (!ancestorOrigin || ancestorOrigin === targetOrigin) {
          window.parent.postMessage(
            {
              type: "global-error-reset",
              error: {
                message: error.message?.slice(0, 2000),
                stack: error.stack?.slice(0, 4000),
                digest: error.digest,
                name: error.name,
              },
              timestamp: Date.now(),
              userAgent: navigator.userAgent?.slice(0, 500),
            },
            targetOrigin
          );
        }
      }
    } catch {
      /* ignore cross-origin access errors */
    }
  }, [error]);

    if (!error) return null;

  return (
    <html lang="en">
      <body className="bg-background text-foreground flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="space-y-2">
            <h1 className="text-destructive text-2xl font-bold">Something went wrong!</h1>
            <p className="text-muted-foreground">An unexpected error occurred.</p>
          </div>
          <div className="space-y-2">
            {process.env["NODE_ENV"] === "development" && (
              <details className="mt-4 text-left">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-sm">
                  Error details
                </summary>
                <pre className="bg-muted mt-2 overflow-auto rounded p-2 text-xs">
                  {error.message}
                  {error.stack && <div className="text-muted-foreground mt-2">{error.stack}</div>}
                  {error.digest && (
                    <div className="text-muted-foreground mt-2">Digest: {error.digest}</div>
                  )}
                </pre>
              </details>
            )}
          </div>
          <button
            onClick={reset}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors"
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
